import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Button, utils } from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import { getRetinaImage, checkJsonObjectMandatory, handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import { CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';
import './AmexResyValueFeaturedHighlights.scss';

const AmexResyValueFeaturedHighlights = (
  {
    awsFolderName,
    baseRequestUrl,
    sectionConfig = {},
    Analytics,
    matchMedia,
  }
) => {
  if (Object.keys(sectionConfig).length === 0) return null;

  const requiredKeys = ['title', 'copy', 'images', 'ctas'];
  const isValidMainContent = checkJsonObjectMandatory(requiredKeys, sectionConfig, 'AmexResyValueFeaturedHighlights');

  if (!isValidMainContent) return null;

  const requiredKeysCta = ['label', 'store_button', 'url'];
  const {
    ctas,
  } = sectionConfig;

  let isValidCtaContent = false;
  ctas?.forEach((element) => {
    isValidCtaContent = checkJsonObjectMandatory(requiredKeysCta, element, 'AmexResyValueFeaturedHighlights');
  });

  if (!isValidCtaContent) return null;

  const {
    title: featuredTitle = '',
    copy: featuredCopy = '',
    images: {
      desktop: desktopImg = '',
      mobile: mobileImg = '',
    } = {},
    qr_alt: qrAlt,
    id: sectionId = 'downloadapp',
  } = sectionConfig;

  const sanitizedFeaturedTitle = sanitizeString(featuredTitle);
  const sanitizedFeaturedCopy = sanitizeString(featuredCopy);

  const isDesktopUX = matchMedia.sm;

  const imgAsset = getRetinaImage(desktopImg, mobileImg, isDesktopUX) || null;
  const featuredImg = `${baseRequestUrl}${awsFolderName}/images/${imgAsset}`;

  const displayCTA = (image) => ctas?.map((element) => {
    if (Object.keys(element).length === 0 || !element?.label) return null;

    return (
      <Button
        key={element?.label}
        variant="no-style"
        type="button"
        className={cx('AmexResyValueFeaturedHighlights__primary cta', {
          mobile: !isDesktopUX,
        })}
        onClick={() => {
          const extraParams = {
            action_name: 'Featured Highlights - Click',
            item_name: element?.label,
          };
          handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraParams);
          utils.openExternalUrl(element?.url, '_blank');
        }}
        data-link="download-link"
      >
        {image && (
          <img
            className="AmexResyValueFeaturedHighlights__logo"
            src={`${baseRequestUrl}${awsFolderName}/images/${element?.store_button}`}
            alt={element?.store_alt}
          />
        )}
        {!image && element?.label}
      </Button>
    );
  });

  return (
    <section className="AmexResyValueFeaturedHighlights">
      <div className="container">
        <div className="AmexResyValueFeaturedHighlights__wrapper">
          <div className="AmexResyValueFeaturedHighlights__copy">
            {featuredTitle && (
              <h2
                id={sectionId}
                className="AmexResyValueFeaturedHighlights__title heading-2"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: sanitizedFeaturedTitle,
                }}
              />
            )}
            {featuredCopy && (
              <h3
                className="AmexResyValueFeaturedHighlights__content heading-3"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: sanitizedFeaturedCopy,
                }}
              />
            )}

            {isDesktopUX && ctas && (
              <div className="AmexResyValueFeaturedHighlights__cta">
                {displayCTA()}
              </div>
            )}

            {!isDesktopUX && ctas && (
              <div className="AmexResyValueFeaturedHighlights__cta">
                {displayCTA(true)}
              </div>
            )}

            {imgAsset && (
              <img
                className="AmexResyValueFeaturedHighlights__image"
                src={featuredImg}
                alt={qrAlt}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

AmexResyValueFeaturedHighlights.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  sectionConfig: PropTypes.shape({
    title: PropTypes.string,
    image: PropTypes.shape({
      desktop: PropTypes.string.isRequired,
      mobile: PropTypes.string.isRequired,
    }),
    cta: PropTypes.shape({}),
    qr_alt: PropTypes.string,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
};

export default AmexResyValueFeaturedHighlights;
